import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, MapPin } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Ticket } from "@/hooks/use-event-data";

interface TicketType {
  id: string;
  name: string;
  price: number;
  description: string;
}

interface OrderSummaryProps {
  selectedTicketType: Ticket | undefined;
  quantity: number;
  eventDate: string;
  eventStartTime: string;
  eventEndTime: string;
  eventLocation: string;
  eventPrice: string;
  virtualEvent: string;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedTicketType,
  quantity,
  eventDate,
  eventStartTime,
  eventEndTime,
  eventLocation,
  eventPrice,
  virtualEvent,
}) => {
  const isMobile = useIsMobile();
  const totalAmount = selectedTicketType
    ? Number(selectedTicketType.price) * quantity
    : Number(eventPrice) * quantity;

  return (
    <Card className="sticky top-4">
      <CardHeader className={`${isMobile ? "py-3 px-4" : "py-4 px-6"}`}>
        <CardTitle className={`${isMobile ? "text-lg" : "text-xl"}`}>
          Order Summary
        </CardTitle>
      </CardHeader>
      <CardContent className={`${isMobile ? "py-3 px-4" : "py-4 px-6"}`}>
        <div className="space-y-3 md:space-y-4">
          <div>
            <h3 className="font-medium">
              {selectedTicketType?.name || "Standard Ticket"}
            </h3>
            <div className="flex justify-between text-sm mt-2">
              <span>Price per ticket</span>
              <span>${selectedTicketType?.price || eventPrice || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Quantity</span>
              <span>{quantity}</span>
            </div>
          </div>

          <Separator />

          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>${totalAmount.toFixed(2)}</span>
          </div>

          <div className="p-2 md:p-3 bg-gray-50 rounded text-xs md:text-sm">
            <p className="font-medium mb-1">Event Information:</p>
            <div className="space-y-2">
              <div className="flex items-start">
                <Calendar className="h-4 w-4 mr-2 mt-0.5 text-gray-500 flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    {new Date(eventDate).toLocaleDateString("en-US", {
                      weekday: isMobile ? "short" : "long",
                      year: "numeric",
                      month: isMobile ? "short" : "long",
                      day: "numeric",
                    })}
                  </p>
                  <p className="text-gray-600">
                    {eventStartTime} - {eventEndTime}
                  </p>
                </div>
              </div>

              {virtualEvent === "No" && (
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 mr-2 mt-0.5 text-gray-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{eventLocation}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
